import lodashDebounce from 'lodash.debounce'

import { Customer } from '../schema'

/**
 * Converts seconds/microseconds timestamps to milliseconds, leaves milliseconds
 * timestamps untouched. Works for timestamps no older than 2001.
 * @timestamp A timestamp that can be seconds, milliseconds or microseconds.
 * Should be no older than 2001.
 */
export function normalizeTimestampToMs(timestamp: number): number {
  if (timestamp === 0) {
    return timestamp
  }

  const t = timestamp.toString()

  if (t.length === 10) {
    // is seconds
    return Number(t) * 1000
  } else if (t.length === 13) {
    // is milliseconds
    return Number(t)
  } else if (t.length === 16) {
    // is microseconds
    return Number(t) / 1000
  }

  log(`normalizeTimestamp() -> could not interpret timestamp -> ${timestamp}`)

  return Number(t)
}

export const EMPTY_OBJ: Readonly<Record<string, any>> = Object.freeze({})
export const emptyObj = EMPTY_OBJ

export const emptyStrArr = '[]'

export const EMPTY_ARRAY: readonly any[] = Object.freeze([])
export const emptyArr: readonly unknown[] = EMPTY_ARRAY
export const emptyArrStr = EMPTY_ARRAY as readonly string[]
export const emptyArrNumber = EMPTY_ARRAY as readonly number[]
// TS being stupid, TODO: fix
export const emptyOpts = emptyArr as Opts

// eslint-disable-next-line @typescript-eslint/no-empty-function
export const EMPTY_FN = () => {}
export const emptyFn = EMPTY_FN

/**
 * `[1, 2, 3, 4, 5]` -> `[[1,2], [3,4], [5]]`. `[]` -> `[]`.
 * @param arr The array to split into pairs.
 */
export const toPairs = <T>(arr: T[]): T[][] => {
  const pairs: T[][] = []

  for (let i = 0; i < arr.length; i++) {
    if (i % 2 === 0) {
      pairs.push([])
    }

    const toPush = arr[i]
    toPush && pairs[Math.floor(i / 2)]?.push(toPush)
  }

  return pairs
}

/**
 * Returns true if different.
 * @param a
 * @param b
 * @returns {boolean}
 */
export const areShallowDifferent = <
  T extends Record<string, unknown>,
  U extends Record<string, unknown>,
>(
  newObj: T,
  prevObj: U,
) => {
  for (const key in newObj) {
    // @ts-expect-error I'll deal with this after merge
    if (newObj[key] !== prevObj[key]) return true
  }
  return false
}

export const processErr = (e: unknown): string => {
  if (typeof e === 'string') return e
  if (
    typeof e === 'object' &&
    e !== null &&
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    typeof (e as any).message === 'string'
  ) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (e as any).message
  }

  log(e)

  return 'Unknown Error (Could not parse error, check console)'
}

type ObjKey = number | string | symbol

export const mapKeys = <T>(
  obj: Record<ObjKey, T>,
  mapper: (val: T, key: ObjKey) => ObjKey,
) => {
  const newObj = {} as Record<ObjKey, T>

  for (const [key, value] of entries(obj)) {
    newObj[mapper(value, key)] = obj[key]!
  }

  return newObj
}

const fromEntries = <K extends PropertyKey, V>(
  entries: Iterable<readonly [K, V]>,
): { [P in K]: V } => {
  return Object.fromEntries(entries) as { [P in K]: V }
}

export const mapValues = <T extends Record<PropertyKey, any>, U>(
  obj: T,
  fn: (value: T[keyof T], key: keyof T) => U,
): { [K in keyof T]: U } =>
  fromEntries(entries(obj).map(([key, value]) => [key, fn(value, key)]))

export const pickBy = <K extends string, T>(
  // Partial<T> is used here to accept Partial<T>s as well, then we cast inside.
  obj: Partial<Record<K, T>>,
  predicate: (value: T, key: K) => boolean,
): Record<K, T> => {
  const newObj = {} as Record<K, T>

  for (const [key, val] of entries(obj)) {
    /**
     * CAST: Typescript limitation 🙄. It's obvious entries() will not bring up
     * non-existing pairs from Partial<T>,
     */
    if (predicate(val as T, key)) {
      // CAST: ditto
      newObj[key] = val as T
    }
  }

  return newObj
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Logger = (...args: any[]) => void

export let log = null as unknown as Logger
export let debug = null as unknown as Logger
export const setLogger = (logger: Logger) => {
  log = logger
}
export const setLoggerDebug = (logger: Logger) => {
  debug = logger
}

export const keys = <T extends object, K extends keyof T>(o: T): K[] => {
  return Object.keys(o) as K[]
}

export type ValueOf<T> = T[keyof T]

export const values = <T>(o: T): ValueOf<T>[] => {
  // TODO: Typescript updated typings for Object.values
  // @ts-expect-error
  return Object.values(o) as ValueOf<T>[]
}

export type Entries<T> = [key: keyof T, value: ValueOf<T>][]

export const entries = <T>(o: T): Entries<T> => {
  // TODO: Typescript updated typings for Object.entries
  // @ts-expect-error
  return Object.entries(o) as Entries<T>
}

export const sortedEntries = <T>(o: T): Entries<T> => {
  const unsortedEntries = entries(o)
  const sortedEntries = unsortedEntries.sort(([keyA], [keyB]) => {
    if (typeof keyA === 'number' && typeof keyB === 'number') {
      return keyA - keyB
    }
    if (typeof keyA === 'string' && typeof keyB === 'string') {
      return keyA.localeCompare(keyB)
    }
    throw new TypeError(`Expected string or number keys.`)
  })
  return sortedEntries
}

export const reverseSortedEntries = <T>(o: T): Entries<T> => {
  const unsortedEntries = entries(o)
  const sortedEntries = unsortedEntries.sort(([keyA], [keyB]) => {
    if (typeof keyB === 'number' && typeof keyA === 'number') {
      return keyB - keyA
    }
    if (typeof keyB === 'string' && typeof keyA === 'string') {
      return keyB.localeCompare(keyA)
    }
    throw new TypeError(`Expected string or number keys.`)
  })
  return sortedEntries
}

export const uriIsVideo = (url: string) => {
  const isImage =
    url.endsWith('.jpg') ||
    url.endsWith('.jpeg') ||
    url.endsWith('.png') ||
    url.endsWith('.webp')

  return !isImage
}

type EventBatcherListener<E> = (events: E[]) => void

export interface EventBatcher<E> {
  eventReceived(event: E): void
  flush(): void
  onEvents(listener: EventBatcherListener<E>): () => void
  off(): void
}

export interface EventBatcherOpts {
  /**
   * Milliseconds.
   */
  debounceTime: number
  /**
   * Emit the first event of a burst immediately instead of waiting out
   * `debounceTime` first. Off by default, which is the original behaviour.
   *
   * Why it exists (Monday 12800619368): on a cold start every incoming event
   * CANCELS the pending flush and restarts the clock, so nothing reaches the
   * screen until a full `debounceTime` passes with no events at all. On the
   * web the events land in a tight burst and that costs ~1s. On a phone each
   * one is spaced out by the 654-field schema validation that runs
   * synchronously per customer, so the timer keeps restarting and the list
   * takes ~10s to appear. With this on, the first event paints straight away
   * and the rest fill in behind it.
   */
  leading?: boolean
  /**
   * Milliseconds. Flush at least this often once events start arriving, no
   * matter how many keep coming. Off by default.
   *
   * Without it the debounce has no ceiling: a steady trickle can hold events
   * back indefinitely, because there is never a quiet `debounceTime` window.
   */
  maxWait?: number
}

export const createEventBatcher = <E>({
  debounceTime,
  leading = false,
  maxWait,
}: EventBatcherOpts): EventBatcher<E> => {
  let pendingEvents: E[] = []

  const listeners = new Set<EventBatcherListener<E>>()

  let currTimeout: NodeJS.Timeout | null = null
  let maxWaitTimeout: NodeJS.Timeout | null = null
  /** Cleared when a burst closes, so the next one gets its own leading edge. */
  let burstStarted = false

  const clearTimers = (): void => {
    if (currTimeout) {
      clearTimeout(currTimeout)
      currTimeout = null
    }
    if (maxWaitTimeout) {
      clearTimeout(maxWaitTimeout)
      maxWaitTimeout = null
    }
  }

  /** Sends whatever is queued, even if that is nothing - as `flush` always did. */
  const emit = (): void => {
    // Swap the array out BEFORE notifying: a listener that itself produces
    // another event must not have it dropped by the reset happening after.
    const toEmit = pendingEvents
    pendingEvents = []
    burstStarted = false
    clearTimers()
    listeners.forEach((listener) => {
      listener(toEmit)
    })
  }

  /**
   * Timer-driven emit. An empty queue closes the burst quietly instead of
   * handing listeners an empty batch - which happens after a leading-edge
   * emit that turned out to be the only event in its burst. Without this the
   * burst flag would stay stuck and the next burst would lose its leading edge.
   */
  const emitPending = (): void => {
    if (pendingEvents.length === 0) {
      burstStarted = false
      clearTimers()
      return
    }
    emit()
  }

  const armTimers = (): void => {
    if (currTimeout) {
      clearTimeout(currTimeout)
    }
    currTimeout = setTimeout(emitPending, debounceTime)

    // Armed once per burst and deliberately never pushed back, so a steady
    // trickle cannot hold events past this ceiling. The null check is doing
    // two jobs: pushing the deadline back would defeat the ceiling, and
    // re-assigning without clearing would leak the previous timer.
    if (maxWait !== undefined && maxWaitTimeout === null) {
      maxWaitTimeout = setTimeout(emitPending, maxWait)
    }
  }

  return {
    eventReceived(event) {
      pendingEvents.push(event)

      const isFirstOfBurst = !burstStarted
      burstStarted = true

      if (leading && isFirstOfBurst) {
        // Straight out, so the screen has something immediately. Costs one
        // extra emit per burst, not one per event.
        emit()
        burstStarted = true
      }

      armTimers()
    },
    flush() {
      emit()
    },
    off() {
      listeners.clear()
    },
    onEvents(listener) {
      listeners.add(listener)
      return () => {
        listeners.delete(listener)
      }
    },
  }
}

export type booleans = readonly boolean[]
export type numbers = readonly number[]
export type strings = readonly string[]

export type EmptyFn = () => void
export type VoidFn = () => void

export type Writable<T> = {
  -readonly [P in keyof T]: T[P]
}

export type DeepReadonly<T> = T extends Array<infer U>
  ? DeepReadonlyArray<U>
  : T extends Function
  ? T
  : T extends object
  ? DeepReadonlyObject<T>
  : T

type DeepReadonlyArray<T> = readonly DeepReadonly<T>[]

type DeepReadonlyObject<T> = Readonly<{
  [P in keyof T]: DeepReadonly<T[P]>
}>

/**
 * TODO: Support tuples
 */
export type r<T> = DeepReadonly<T>

export type RRecord<
  K extends number | string | symbol = string | number | symbol,
  T = unknown,
> = r<Record<K, T>>

export type Dict<
  T = unknown,
  K extends number | string | symbol = string | number | symbol,
> = Readonly<Record<K, T>>

export type DictW<
  T,
  K extends number | string | symbol = string | number | symbol,
> = Record<K, T>

export type DictB<T, K extends number | string | symbol = string> = r<
  Record<K, T>
>

/**
 * Simplest Dict, string keys
 */
export type DictS<T> = Readonly<Record<string, T>>

export type ReadonlyRecord<T, K extends string = string> = Readonly<
  Record<K, T>
>

export type ReadonlyRecordPartial<T, K extends string = string> = Partial<
  ReadonlyRecord<T, K>
>

export type Primitive = boolean | number | string

/**
 * @param size
 * @param startAt Lowest number, inclusive.
 */
export const range = (size: number, startAt = 0): number[] =>
  new Array(size).fill(null).map((_, i) => i + startAt)

interface IsBetweenParams {
  readonly lowerBound: number
  readonly upperBound: number
  readonly value: number
}

export const isBetween = ({
  lowerBound,
  upperBound,
  value,
}: IsBetweenParams): boolean => value >= lowerBound && value <= upperBound

const NUMBER_CHARS: ReadonlyArray<string> = '0123456789'.split('')

/**
 * The `length > 0` is not decoration. `''.split('')` is `[]` and `[].every()`
 * is `true`, so an empty string used to answer yes to "is this an integer?".
 * `useMpuCalculator` asks exactly that before running the Main Panel Upgrade
 * calculator, and so ran it on empty fields.
 */
export const stringIsInt = (numberStr: string): boolean =>
  typeof numberStr === 'string' &&
  numberStr.length > 0 &&
  numberStr.split('').every((char) => NUMBER_CHARS.includes(char))

/** Same empty-string trap as `stringIsInt` above. */
export const stringIsFloating = (numberStr: string): boolean =>
  typeof numberStr === 'string' &&
  numberStr.length > 0 &&
  numberStr
    .split('')
    .every((char) => NUMBER_CHARS.includes(char) || char === '.')

export const phoneValidationUrl = 'https://phonevalidation.abstractapi.com/v1'

export const isValidFBKey = (key: unknown) => {
  if (typeof key !== 'string') {
    return false
  }
  if (key.length !== 20) {
    return false
  }
  return true
}

export const numberToMonth: Record<number, string> = {
  0: 'Jan',
  1: 'Feb',
  2: 'Mar',
  3: 'Apr',
  4: 'May',
  5: 'Jun',
  6: 'Jul',
  7: 'Aug',
  8: 'Sep',
  9: 'Oct',
  10: 'Nov',
  11: 'Dec',
}

export const isNumber = (value: unknown): boolean => {
  let regExp = new RegExp(/^\d*\.?\d*$/)

  if (regExp.test(String(value))) {
    return true
  } else {
    return false
  }
}

export const zip = <T, U>(arr1: T[], arr2: U[]): readonly [T, U][] => {
  const len = arr1.length
  if (len !== arr2.length) {
    throw new Error(
      `zip() -> arrays not the same length (${arr1.length},${arr2.length})`,
    )
  }
  if (len === 0) {
    return EMPTY_ARRAY as readonly [T, U][]
  }
  const res: [T, U][] = []
  for (let i = 0; i < len; i++) {
    // CAST: Length checked above so never empty
    res[i] = [arr1[i]!, arr2[i]!]
  }
  return res
}

export type Zipper<T, U, V> = (a: T, b: U) => V

export const zipWith = <T, U, V>(
  arr1: T[],
  arr2: U[],
  zipper: Zipper<T, U, V>,
): readonly V[] => {
  const zipped = zip(arr1, arr2)

  return zipped.map(([a, b]) => zipper(a, b))
}

export const zeroFill = (number: number, width: number) => {
  const numberOutput = Math.abs(number)
  const length = number.toString().length
  const zero = '0'

  if (width <= length) {
    if (number < 0) {
      return '-' + numberOutput.toString()
    } else {
      return numberOutput.toString()
    }
  } else {
    if (number < 0) {
      return '-' + zero.repeat(width - length) + numberOutput.toString()
    } else {
      return zero.repeat(width - length) + numberOutput.toString()
    }
  }
}

export interface DateInfo {
  readonly day: string
  readonly hour: string
  readonly minutes: string
  readonly month: string
  readonly seconds: string
  readonly year: string
}

export const getDateInfo = (timestamp: number): DateInfo => {
  const date = new Date(normalizeTimestampToMs(timestamp))

  const month =
    (date.getUTCMonth() + 1).toString().length === 1
      ? '0' + (date.getUTCMonth() + 1).toString()
      : (date.getUTCMonth() + 1).toString()
  const day =
    date.getUTCDate().toString().length === 1
      ? '0' + date.getUTCDate().toString()
      : date.getUTCDate().toString()
  const year = date.getFullYear().toString()
  const hour = date.getHours().toString()
  const minutes = date.getMinutes().toString()
  const seconds = date.getSeconds().toString()

  return {
    day,
    hour,
    minutes,
    month,
    seconds,
    year,
  }
}

export const isObj = (
  o: unknown,
): o is Record<number | string | symbol, unknown> =>
  typeof o === 'object' && o !== null

/**
 * Strips the punctuation people type into a phone number, so what is stored
 * and validated is just the digits.
 *
 * Every step used to be `.replace(' ', '')` with a string argument, which
 * removes the FIRST match and nothing else. One space and one dash survived
 * the pass, which is why the app rejected the very format its own error
 * message asks for: `(786) 786 - 7867` came out as `786786  7867`, twelve
 * characters, and the rep was told the number "should be 10 digits long".
 *
 * It also made the function non-idempotent, and `isValidUSPhoneNumber` throws
 * on input it can still normalize further — so a nine-digit typo like
 * `801-123-123` landed on exactly ten characters, cleared the length check,
 * and put that function's internal reminder to developers on screen in front
 * of a rep.
 *
 * Dots are deliberately left alone: `702.555.9999` is pinned as passing
 * through untouched in sanitizeCustomer.test.ts.
 */
export const normalizePhoneNumber = (phone: string): string =>
  phone.replace(/[()\s-]/g, '')

/**
 * Strips `-`, `+` and `,` from a number/decimal-pad text input as the rep
 * types, so a negative sign, stray plus, or thousands separator never reaches
 * a numeric field. Found by /bug-hunt (2026-09-07): the version this replaced
 * chained `.replace('-', '').replace('+', '').replace(',', '')` — each call
 * with a STRING argument, which only removes the FIRST match. Same shape as
 * `normalizePhoneNumber`'s bug above: typing or pasting a thousands-separated
 * number ("1,234,567") left every comma after the first in the field.
 */
export const stripNumericInputSymbols = (text: string): string =>
  text.replace(/[-+,]/g, '')

/**
 * `windowColors` lives in `Misc/env` as a JSON STRING (`'["White","Black"]'`),
 * but the Admin panel's Env tab runs `JSON.parse` on whatever an admin saves,
 * so re-saving that key writes a real ARRAY. `JSON.parse(array)` coerces it to
 * "White,Black" and throws, and the two callers had no guard - a blank customer
 * screen. Accept either shape and never throw. Found by /bug-hunt (2026-09-29).
 */
export const parseEnvStringList = (raw: unknown): string[] => {
  if (Array.isArray(raw)) return raw.map(String)
  if (typeof raw !== 'string' || raw === '') return []
  try {
    const parsed: unknown = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed.map(String) : []
  } catch {
    return []
  }
}
export const isValidUSPhoneNumber = (usPhoneNumber: string): boolean => {
  const normalized = normalizePhoneNumber(usPhoneNumber)

  if (usPhoneNumber !== normalized) {
    throw new TypeError(
      `Normalize phone number before passing it to isValidUSPhoneNumber(), this is to avoid bugs not in here but elsewhere as this hopefully reminds you of normalizing everywhere.`,
    )
  }

  if (normalized.length !== 10) {
    return false
  }

  const allCharNumbers = stringIsInt(normalized)
  const someCharsNotNumbers = !allCharNumbers

  if (someCharsNotNumbers) {
    return false
  }

  const areaCode = Number(normalized.slice(0, 3))

  return areaCode >= 201 && areaCode <= 989
}

/**
 * `customerAddress` is a single freeform string (no separate
 * street/city/state/zip fields exist in the schema) — some contract layouts
 * (e.g. MSE's Home Improvement Agreement, Slack 2026-08-07 "Maria G colocó
 * ya la dirección completa") need it split into those 4 parts. Expects the
 * conventional "Street, City, State Zip" comma format; any part missing
 * from the source string (state/zip are frequently omitted) just comes back
 * empty rather than throwing — the contract still renders, blank lines and
 * all, exactly like the paper form would.
 */
export const splitAddress = (
  fullAddress: string,
): { city: string; state: string; street: string; zip: string } => {
  const parts = fullAddress
    .split(',')
    .map((p) => p.trim())
    .filter(Boolean)

  if (parts.length === 0) return { city: '', state: '', street: '', zip: '' }
  if (parts.length === 1) {
    return { city: '', state: '', street: parts[0]!, zip: '' }
  }

  const hasStateZip = parts.length >= 3
  const stateZipPart = hasStateZip ? parts[parts.length - 1]! : ''
  const city = parts[hasStateZip ? parts.length - 2 : parts.length - 1]!
  const street = parts.slice(0, hasStateZip ? -2 : -1).join(', ')

  const stateZipMatch = stateZipPart.match(
    /^([A-Za-z]{2,})\s+(\d{5}(-\d{4})?)$/,
  )

  return {
    city,
    state: stateZipMatch ? stateZipMatch[1]! : stateZipPart,
    street,
    zip: stateZipMatch ? stateZipMatch[2]! : '',
  }
}

export const numberToDate = (data: number): string => {
  const date = normalizeTimestampToMs(data as number)

  let year = new Date(date).getUTCFullYear().toString()

  let day = new Date(date).getUTCDate().toString()
  day = day.length === 1 ? '0' + day : day

  let month = (new Date(date).getUTCMonth() + 1).toString()
  month = month.length === 1 ? '0' + month : month

  return `${year}-${month}-${day}`
}

export const uniq = (v: any, i: number, a: readonly any[]): boolean =>
  a.indexOf(v) === i

export const getObjectDifferences = (obj1: any, obj2: any): string[] => {
  const keys1 = Object.keys(obj1)
  const keys2 = Object.keys(obj2)

  const allKeys = [...keys1, ...keys2].filter(uniq)

  const differences: any[] = []

  allKeys.forEach((key) => {
    if (obj1[key] !== obj2[key] && typeof obj1[key] !== 'object') {
      differences.push(key)
    } else if (
      typeof obj1[key] === 'object' &&
      JSON.stringify(obj1[key]) !== JSON.stringify(obj2[key])
    ) {
      differences.push(key)
    }
  })

  return differences
}

export const isArray = <T>(o: readonly T[] | unknown): o is readonly T[] =>
  Array.isArray(o)

export const strEmptyArr = '[]' as const

export const zero = 0
export const minusOne = -1

export const zeroBoxed: readonly [0] = Object.freeze([0])

/**
 * Min and max inclusive
 */
export const randomIntFromInterval = (min: number, max: number): number => {
  return Math.floor(Math.random() * (max - min + 1) + min)
}

/**
 * Creates a random color hex string, e.g. `#00AA11`.
 */
export const rndColor = (): string => {
  const a = randomIntFromInterval(0, 15).toString(16).toUpperCase()
  const b = randomIntFromInterval(0, 15).toString(16).toUpperCase()
  const c = randomIntFromInterval(0, 15).toString(16).toUpperCase()

  return `#${a}${b}${c}`
}

/**
 * Drops the thousands separators a rep typed before the number is parsed.
 *
 * `Number.parseFloat('7,900')` stops at the comma and returns 7. Costs are
 * stored as whatever string was typed into the field, and 11 of them across 7
 * customers carry a comma - a $7,900 air conditioner counted as $7 in the
 * contract TOTAL, a $12,760 roof as $12. $98,726 misread across the base,
 * measured over all 7,151 customers.
 *
 * Safe against the decimal-comma reading: exactly one stored cost has a comma
 * followed by fewer than three digits, `"0,00"`, and that is zero either way.
 * Trailing and leading spaces go too - four records carry those.
 */
const limpiarNumero = (s: string): string => s.trim().replace(/,/g, '')

export const parseInteger = (o: unknown): number => {
  try {
    let res: number = zero
    if (o === null) return zero
    if (typeof o === 'undefined') return zero
    if (typeof o === 'boolean' && !o) return zero
    if (typeof o === 'string') res = Number.parseInt(limpiarNumero(o), 10)
    if (typeof o === 'number') res = o
    if (Number.isNaN(res)) return zero
    else return res
  } catch {
    return 0
  }
}

export const parseInt = parseInteger

export const parseFloating = (o: unknown): number => {
  try {
    let res: number = zero
    if (o === null) return zero
    if (typeof o === 'undefined') return zero
    if (typeof o === 'boolean' && !o) return zero
    if (typeof o === 'string') res = Number.parseFloat(limpiarNumero(o))
    if (typeof o === 'number') res = o
    if (Number.isNaN(res)) return zero
    else return res
  } catch {
    return 0
  }
}

export const placeholderTextColorDark = 'rgba(255,255,255,0.5)'

export const placeholderTextColorLight = 'rgba(0,0,0,0.4)'

export const zeroes = ['0', 0]

export function debounce<T extends (...args: any[]) => void>(
  func: T,
  wait: number = 350,
) {
  return lodashDebounce(func, wait)
}

export const isNoteField = (field: keyof Customer) =>
  field.endsWith('Notes') || field.endsWith('notes')

export const identity = <T>(_: T): T => _

export interface Opt<T extends string = string> {
  readonly label: string
  readonly value: T
}
export type Opts<T extends string = string> = readonly Opt<T>[]
export const naOpt: Opt = Object.freeze({
  label: 'n/a',
  value: 'n/a',
})
export const optsWithOnlyNA: Opts = Object.freeze([naOpt])

export const delay = (ms: number): Promise<void> =>
  new Promise<void>((res) => {
    setTimeout(res, ms)
  })

export const add = (a: number, b: number): number => a + b

export const capitalizeFirstLetter = (str: string) => {
  return String(str).charAt(0).toUpperCase() + String(str).slice(1)
}

export const resizeArray = <T>(
  arr: readonly T[],
  newLength: number,
  fillValue: T,
): T[] => {
  if (newLength < arr.length) {
    // Crop
    return arr.slice(0, newLength)
  } else {
    // Expand
    return arr.concat(Array(newLength - arr.length).fill(fillValue))
  }
}

export type InputEvent = { target: { value: string } }

export const blankChar = (qty: number): string =>
  new Array(qty).fill(String.fromCharCode(160)).join('')

export const alwaysNull = (): null => null

/**
 * Valid DB value as it can appear in Firebase.
 */
export type DBVal = readonly unknown[] | undefined | Primitive | DBValues

/**
 * Valid DB values map as it can appear in Firebase.
 */
export interface DBValues extends Readonly<Record<string, DBVal>> {}
